import { Badge, Tab, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { AllocationSetupEntitiesTable } from "features/allocations/setup/AllocationSetupEntitiesTable";
import { AllocationSetupInvestorsTable } from "features/allocations/setup/AllocationSetupInvestorsTable";
import { AllocationRuleChooseInvestingEntityData } from "gen/model/allocationRuleChooseInvestingEntityData";
import { AllocationRuleInvestingEntitiesData } from "gen/model/allocationRuleInvestingEntitiesData";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBadges } from "shared/components/table/box/TableBoxBadges";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTabs } from "shared/components/table/box/TableBoxTabs";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import {
  OnChangeFn,
  RowSelectionState,
} from "shared/components/table/data/DataTableTypes";
import { useQueryParam } from "use-query-params";

export interface AllocationEntitiesTableProps {
  fundFamilyId: string;
  allocationRuleId: string;
  entityAllocationsData: AllocationRuleChooseInvestingEntityData[];
  currentInvestingEntityData: AllocationRuleInvestingEntitiesData;
  numSelectedInvestors: number;
  numSelectedEntities: number;
  investorsRowSelectionState: RowSelectionState;
  entitiesRowSelectionState: RowSelectionState;
  onInvestorsRowSelectionChanged: OnChangeFn<RowSelectionState>;
  onEntitiesRowSelectionChanged: OnChangeFn<RowSelectionState>;
  isEditable?: boolean;
}

const TAB_IDS = ["investors", "entities"] as const;
type TabId = (typeof TAB_IDS)[number];

export const AllocationEntitiesTable: React.FC<
  AllocationEntitiesTableProps
> = ({
  fundFamilyId,
  allocationRuleId,
  entityAllocationsData,
  currentInvestingEntityData,
  investorsRowSelectionState,
  entitiesRowSelectionState,
  numSelectedInvestors,
  numSelectedEntities,
  onInvestorsRowSelectionChanged,
  onEntitiesRowSelectionChanged,
  isEditable = true,
}) => {
  const { t } = useTranslation(["allocation"]);

  const [selectedTab, setSelectedTab] = useQueryParam<TabId | null>("view");

  const defaultTab: TabId = currentInvestingEntityData.commitments.rows.length
    ? "investors"
    : "entities";

  const tabIndex = TAB_IDS.indexOf(selectedTab || defaultTab);

  const onChangeTab = useCallback(
    (index: number) => setSelectedTab(TAB_IDS[index]),
    [setSelectedTab],
  );

  const isNotEmpty =
    currentInvestingEntityData.commitments.rows.length > 0 ||
    currentInvestingEntityData.entities.length > 0;

  // A TableBox can only contain a single AG Grid component.
  // isLazy={true} and key={selectedTab} are used to ensure only
  // one AG Grid component is rendered at a time.
  return (
    isNotEmpty && (
      <TableBox key={selectedTab}>
        <TableBoxHeader>
          <TableBoxTabs index={tabIndex} onChange={onChangeTab}>
            <Tab>{t("allocation:setup.entities.investors.title")}</Tab>
            <Tab>{t("allocation:setup.entities.entities.title")}</Tab>
          </TableBoxTabs>
          <TableBoxBadges>
            {numSelectedInvestors > 0 && (
              <Badge>
                {t("allocation:setup.entities.xInvestorsCount", {
                  count: numSelectedInvestors,
                })}
              </Badge>
            )}
            {numSelectedEntities > 0 && (
              <Badge>
                {t("allocation:setup.entities.xRulesSelected", {
                  count: numSelectedEntities,
                })}
              </Badge>
            )}
          </TableBoxBadges>
          <DataTableBoxActions />
        </TableBoxHeader>

        <TableBoxBody>
          <Tabs variant="table" index={tabIndex} isLazy={true}>
            <TabPanels>
              <TabPanel>
                <AllocationSetupInvestorsTable
                  allocationRuleId={allocationRuleId}
                  currentInvestingEntityData={currentInvestingEntityData}
                  fundFamilyId={fundFamilyId}
                  rowSelection={investorsRowSelectionState}
                  onRowSelectionChanged={onInvestorsRowSelectionChanged}
                />
              </TabPanel>
              <TabPanel>
                <AllocationSetupEntitiesTable
                  isEditable={isEditable}
                  entityAllocationsData={entityAllocationsData}
                  currentInvestingEntityData={currentInvestingEntityData}
                  fundFamilyId={fundFamilyId}
                  rowSelection={entitiesRowSelectionState}
                  onRowSelectionChanged={onEntitiesRowSelectionChanged}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </TableBoxBody>
      </TableBox>
    )
  );
};
